/**
 * Plain-text description of what a run covers — date range, filters and
 * grouping — printed above the table and written as a preamble in the CSV,
 * so a printout or export can't be misread out of context.
 */
import {
  ReportV2Definition,
  RunReportV2Response,
  ViewConfig,
} from "../../../../types/reportsV2";
import { dateRangeLabel } from "../../../../utils/dateRangeFormat";
import { FIXED_DATES_PRESET } from "./viewConfig";

export interface RunContext {
  /** "Last 7 days (Oct 1, 2026 – Oct 7, 2026)". */
  range: string;
  /** "Staff: Ana Lee, Ben Ortiz; Booking channel: Walk-in", or "None". */
  filters: string;
  /** "Day → Staff name". */
  groupedBy: string;
}

const NO_FILTERS = "None";

/**
 * @param view the view the run was made for (its filters; the response
 *   doesn't echo them). Callers only describe a settled, current result.
 */
export function describeRun(
  definition: ReportV2Definition,
  data: RunReportV2Response,
  view: ViewConfig,
): RunContext {
  const { preset, start, end } = data.date_range;
  const dates = dateRangeLabel(FIXED_DATES_PRESET, start, end);
  const range =
    preset === FIXED_DATES_PRESET
      ? dates
      : `${dateRangeLabel(preset, null, null)} (${dates})`;

  const filters = definition.filters
    .filter((f) => (view.filters[f.key] ?? []).length > 0)
    .map((f) => {
      const labels = new Map(
        (data.facets[f.key] ?? []).map((o) => [o.value, o.label]),
      );
      const values = view.filters[f.key].map(
        (v) => labels.get(v) ?? (v === "" ? f.empty_label : v),
      );
      return `${f.label}: ${values.join(", ")}`;
    })
    .join("; ");

  const granularity =
    definition.granularities.find((g) => g.key === data.granularity)?.label ??
    data.granularity;
  const groups = data.groups.map(
    (key) => definition.group_bys.find((g) => g.key === key)?.label ?? key,
  );

  return {
    range,
    filters: filters || NO_FILTERS,
    groupedBy: [granularity, ...groups].join(" → "),
  };
}
